import {useEffect, useRef} from 'react'
import {useTranslation} from 'react-i18next'
import type {QueryObserverResult} from '@tanstack/react-query'
import {publicarExito} from '@/lib/avisos'
import type {IngestaJob, ResumenRevision} from '@/hooks/bateria/use-bateria'

function esTerminal(job: IngestaJob): boolean {
  return job.estado === 'completo' || job.estado === 'completo_con_pendientes'
}

/** Solo avisa transiciones vistas en vivo (adenda §8.1): las tandas ya terminadas al abrir se siembran calladas.
 * Pide el resumen al terminar la tanda: el que ya estaba puede no contar aún las hojas del lote. */
export function useAvisoLecturaLista(
  evaluacionId: string | undefined,
  jobs: IngestaJob[] | undefined,
  refrescarResumen: () => Promise<QueryObserverResult<ResumenRevision>>,
  alRevisar: () => void
): void {
  const {t} = useTranslation()
  const terminadas = useRef<{evaluacion: string | undefined; ids: Set<string>} | null>(null)
  // Por ref para que el efecto dependa solo de los jobs.
  const ultimo = useRef({refrescarResumen, alRevisar, t})
  ultimo.current = {refrescarResumen, alRevisar, t}

  useEffect(() => {
    // Sin cargar no se siembra: con una lista vacía, las tandas viejas pasarían por recién terminadas.
    if (jobs === undefined) return
    const ahoraTerminadas = new Set(jobs.filter(esTerminal).map((job) => job.id))
    const previas = terminadas.current
    terminadas.current = {evaluacion: evaluacionId, ids: ahoraTerminadas}
    // Otra evaluación también se siembra callada: sus tandas no terminaron ante esta pantalla.
    if (previas === null || previas.evaluacion !== evaluacionId) return
    const reciennterminadas = [...ahoraTerminadas].filter((id) => !previas.ids.has(id))
    if (reciennterminadas.length === 0) return
    const {refrescarResumen: refrescar, alRevisar: revisar, t: traducir} = ultimo.current
    void refrescar().then((resumen) => {
      if (resumen.status !== 'success') return
      // `pendientes` ya incluye las hojas por ubicar: no sumarle la cuarentena.
      const pendientes = resumen.data.pendientes
      publicarExito(
        traducir('admin.bateria.avisoLectura.titulo'),
        pendientes > 0
          ? traducir('admin.bateria.avisoLectura.conDudas', {count: pendientes})
          : traducir('admin.bateria.avisoLectura.sinDudas'),
        {
          testId: 'aviso-lectura-lista',
          accion:
            pendientes > 0
              ? {etiqueta: traducir('admin.bateria.avisoLectura.accion', {count: pendientes}), alActivar: revisar}
              : undefined,
        }
      )
    })
  }, [evaluacionId, jobs])
}
